import React, { useState, useEffect, useCallback } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { AsyncPaginate } from "react-select-async-paginate";

import Modal from "./components/Modal";

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'BlairITCBold';
    src: url('/fonts/BlairITC-Bold.otf') format('opentype');
    font-weight: bold;
    font-style: normal;
  }

  @font-face {
    font-family: 'BlairITCLight';
    src: url('/fonts/BlairITC-Light.otf') format('opentype');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: 'BlairITCMedium';
    src: url('/fonts/BlairITC-Medium.otf') format('opentype');
    font-weight: medium;
    font-style: normal;
  }

  body {
    font-family: 'BlairITC', sans-serif;
  }

  h2 {
    font-family: 'BlairITCBold', sans-serif;
  }

  h3 {
    font-family: 'BlairITCMedium', sans-serif;
  }

  button {
    font-family: 'BlairITCLight', sans-serif;
  }

  .custom-select__indicator {
    padding: 6px 8px !important;
  }

  .custom-select__control {
    cursor: pointer !important;
  }

  .css-tj5bde-Svg {
    height: 33px;
    width: 33px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-around;
  }
`;

const Column = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px;

  @media (min-width: 768px) {
    margin: 0 10px;
  }
`;

const CanvasWrapper = styled.div`
  width: 100%;
  height: 60vh;

  @media (min-width: 768px) {
    width: 40vw;
    height: 65vh;
    margin-right: 15%;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background-color: #f0f0f0;
  border-radius: 6px;
  width: 100%;
  max-width: 400px;
  border: 1px solid #000;
  box-shadow: 4px 4px #737373;

  @media (min-width: 768px) {
    margin-left: 15%;
  }
`;

const Heading = styled.h2`
  color: #a00;
  text-align: center;
  font-family: "BlairITCMedium", sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 5px;
`;

const Subheading = styled.h3`
  margin-bottom: 20px;
  text-align: center;
  margin-top: 0px;
  font-family: "BlairITCBold", sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
`;

const PreviewButton = styled.button`
  margin-top: 20px;
  padding: 10px 80px;
  background-color: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: "BlairITCMedium", sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 10px;
  width: calc(100% - 30px);
  text-align: center;
  text-transform: uppercase;

  &:hover {
    background-color: #323232;
  }

  ${props => props.appMode === "claim" && `
    padding: 10px 20px;
  `}

  &:disabled {
    background-color: #737373;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    width: calc(100% - 100px);
  }
`;

const ProgressBarContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 6px;
  width: 50%;
  background-color: #f3f3f3;
  border: 1px solid #000;
  border-radius: 4px;
`;

const ProgressBar = styled.div`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background-color: #a00;
`;

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    width: "100%",
    borderRadius: "4px",
    border: "1px solid transparent",
    boxShadow: "none",
    "&:hover": {
      border: "1px solid #000",
    },
  }),
  container: (provided) => ({
    ...provided,
    width: "calc(100% - 30px)",
    "@media (min-width: 768px)": {
      width: "calc(100% - 100px)",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0",
    fontFamily: "BlairITCLight",
    marginLeft: "10px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0",
    padding: "0",
    caretColor: "transparent",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#000",
    fontFamily: "BlairITCLight",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#000",
    fontFamily: "BlairITCLight",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#000",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "4px",
    border: "1px solid #000",
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f0f0f0" : "#fff",
    color: "#000",
    fontFamily: "BlairITCLight",
    "&:hover": {
      backgroundColor: "#f0f0f0",
      cursor: "pointer",
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    color: "#000",
    fontFamily: "BlairITCLight",
    textAlign: "center",
    fontSize: "13px",
  }),
  loadingMessage: (provided) => ({
    ...provided,
    color: "#000",
    fontFamily: "BlairITCLight",
    textAlign: "center",
    fontSize: "13px",
  }),
};

const App = () => {
  const getQueryParams = useCallback((lookingFor) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(lookingFor);
  }, []);

  const initialPunkId = getQueryParams('punkId') || "4770";
  const initialAppMode = getQueryParams('appMode') || "mint";
  const [punkId, setPunkId] = useState(initialPunkId);
  const [appMode, setAppMode] = useState(initialAppMode);
  const [selectedPunk, setSelectedPunk] = useState(null);
  const [modelViewer, setModelViewer] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isClaimingModalOpen, setIsClaimingModalOpen] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);

  const formatOptionLabel = ({ value, label, image }) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        src={image}
        alt={label}
        style={{ width: 26, height: 26, marginRight: 8 }}
      />
      <div>{label}</div>
    </div>
  );

  const loadPunkOptions = async (search, loadedOptions, { page }) => {
    const startIndex = page * 500;
    const endIndex = Math.min(startIndex + 500, 10000);

    let newOptions = [];

    for (let i = startIndex; i < endIndex; i++) {
      const idFormatted = i < 1000 ? i.toString().padStart(3, "0") : i.toString().padStart(4, "0");
      if (idFormatted.includes(search)) {
        newOptions.push({
          value: idFormatted,
          label: `Punk ${idFormatted}`,
          image: `/assets/punk${idFormatted}.png`,
        });
      }
    }

    return {
      options: newOptions,
      hasMore: endIndex < 10000,
      additional: {
        page: page + 1,
      },
    };
  };

  const loadPunkImage = useCallback((id) => {
    const imageUrl = `/assets/punk${id}.png`;
    const image = new Image();
    image.src = imageUrl;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const targetWidth = 256;
      const targetHeight = 266;
      const sourceSize = 24;

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      context.imageSmoothingEnabled = false;

      context.drawImage(
        image,
        0,
        0,
        sourceSize,
        sourceSize,
        0,
        5,
        targetWidth,
        targetHeight - 10
      );

      const scaledImageUrl = canvas.toDataURL();

      if (modelViewer) {
        modelViewer.createTexture(scaledImageUrl).then((newTexture) => {
          const material = modelViewer.model.materials.find(
            (mat) => mat.name === "Default"
          );
          if (material) {
            material.setAlphaMode("BLEND");
            material.pbrMetallicRoughness.baseColorTexture.setTexture(
              newTexture
            );
          }
        });
      }
    };
  }, [modelViewer]);

  useEffect(() => {
    const viewer = document.querySelector("#modelViewer");
    setModelViewer(viewer);

    const preloadImage = new Image();
    const preloadPunkId = initialPunkId || "4770";
    preloadImage.src = `/assets/punk${preloadPunkId}.png`;

    preloadImage.onload = () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const targetWidth = 256;
      const targetHeight = 266;
      const sourceSize = 24;

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      context.imageSmoothingEnabled = false;

      context.drawImage(
        preloadImage,
        0,
        0,
        sourceSize,
        sourceSize,
        0,
        5,
        targetWidth,
        targetHeight - 10
      );

      const scaledImageUrl = canvas.toDataURL();

      const handleModelLoad = () => {
        viewer.createTexture(scaledImageUrl).then((newTexture) => {
          const material = viewer.model.materials.find(
            (mat) => mat.name === "Default"
          );
          if (material) {
            material.setAlphaMode("BLEND");
            material.pbrMetallicRoughness.baseColorTexture.setTexture(
              newTexture
            );
          }
        });
      };

      if (viewer.model) {
        handleModelLoad();
      } else {
        viewer.addEventListener('load', handleModelLoad, { once: true });
      }
    };

    const handleProgress = (event) => {
      const progress = event.detail.totalProgress * 100;
      setLoadingProgress((prevProgress) => Math.max(prevProgress, progress));
      if (progress === 100) {
        setLoading(false);
      }
    };

    viewer.addEventListener('progress', handleProgress);

    const fakeProgressInterval = setInterval(() => {
      setLoadingProgress((prevProgress) => {
        const newProgress = prevProgress + Math.random() * 5;
        return newProgress >= 95 ? 95 : newProgress;
      });
    }, 100);

    return () => {
      viewer.removeEventListener('progress', handleProgress);
      clearInterval(fakeProgressInterval);
    };
  }, [initialPunkId]);

  useEffect(() => {
    if (punkId) {
      loadPunkImage(punkId);
    }
  }, [punkId]);

  useEffect(() => {
    addSignature();
  }, []);

  const addSignature = useCallback(() => {
    if (!document.querySelector("#signature-comment")) {
      const signatureComment = document.createComment(
        " Made with ❤️ by Matt (https://github.com/mattpilleul) "
      );
      signatureComment.id = "signature-comment";
      document.body.appendChild(signatureComment);
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.walletAddress) {
        setWalletAddress(event.data.walletAddress);
        checkSubmission(event.data.walletAddress);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const checkSubmission = async (walletAddress) => {
    alert(walletAddress);
  };

  return (
    <>
      <GlobalStyle />
      <Container>
        <RowContainer>
          <Column>
            <InputWrapper>
              <Heading>Your Punk, Your Watch</Heading>
              <Subheading>Select Your CryptoPunk</Subheading>
              <AsyncPaginate
                classNamePrefix="custom-select"
                placeholder="Select a Punk"
                noOptionsMessage={() => "No matching Punk found"}
                loadingMessage={() => "Loading Punk(s)"}
                debounceTimeout={300}
                value={selectedPunk}
                onChange={(option) => setSelectedPunk(option)}
                loadOptions={loadPunkOptions}
                additional={{
                  page: 0,
                }}
                inputValue={inputValue}
                onInputChange={(newValue) => {
                  setInputValue(newValue);
                }}
                formatOptionLabel={formatOptionLabel}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                styles={customSelectStyles}
                isLoading={loading}
                isDisabled={loading}
              />
              <PreviewButton
                // disabled={loading || !selectedPunk}
                disabled
                appMode={appMode}
                onClick={() => {
                  if (appMode === "mint") {
                    if (selectedPunk) {
                      setPunkId(selectedPunk.value);
                    }
                  } else if (appMode === "claim") {
                    if (selectedPunk) {
                      console.log("Claiming punk with id:", selectedPunk.value);
                      setIsClaimingModalOpen(true);
                    }
                  }
                }}
              >
                {appMode === "mint" ? "Preview" : "Claim Your Watch"}
              </PreviewButton>
            </InputWrapper>
          </Column>
          <Column>
            <CanvasWrapper>
              <model-viewer
                id="modelViewer"
                src="watchCompressed.glb"
                camera-controls
                tone-mapping="neutral"
                disable-tap
                disable-zoom
                loading="eager"
                interaction-prompt="none"
              >
                {loading && (
                  <ProgressBarContainer slot="progress-bar">
                    <ProgressBar progress={loadingProgress} />
                  </ProgressBarContainer>
                )}
              </model-viewer>
            </CanvasWrapper>
          </Column>
        </RowContainer>
        <Modal
          isOpen={isClaimingModalOpen}
          onClose={() => setIsClaimingModalOpen(false)}
          punkId={selectedPunk?.value}
        />
      </Container>
    </>
  );
};

export default App;
